import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO, { type Crumb } from '@/components/SEO';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import CTABand from '@/components/CTABand';
import { staticPage } from '@/lib/content';
import { tools } from '@/tools/registry';
import ResourceFilters from '@/components/ResourceFilters';
import { EMPTY_FILTERS, filterResources, resourceFacets, resourceProfile } from '@/lib/resource-profiles';

const resources = tools.map((tool) => ({ ...tool, path: `/tools/${tool.slug}` }));
const facets = resourceFacets(resources);

const Tools = () => {
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const filteredTools = useMemo(() => filterResources(resources, filters), [filters]);
  const page = staticPage('/tools');
  const crumbs: Crumb[] = [
    { name: 'Home', href: '/' },
    { name: 'Tools', href: '/tools' },
  ];
  return (
    <>
      <SEO title={page.title} description={page.description} path="/tools" breadcrumbs={crumbs} />
      <PageHeader
        crumbs={crumbs}
        label="Tools"
        title="Free tools for WebCenter administrators"
        lead="Browser-based, no sign-up, nothing leaves your machine. Built for the questions that come up in every estate review."
      />
      <section className="container py-14 md:py-16">
        {tools.length > 0 && <ResourceFilters value={filters} onChange={setFilters} facets={facets} count={filteredTools.length} total={tools.length} noun="tools and references" />}
        {tools.length === 0 ? (
          <EmptyState
            heading="Tools are being added."
            body="The component inventory, content inspector, Forms Recognition template analyzer, TCO calculator, decision guide and the Fusion Middleware support-dates table are being ported now."
          />
        ) : (
          <ul className="grid gap-px border border-rule bg-rule md:grid-cols-2">
            {filteredTools.map((t) => (
              <li key={t.slug} className="bg-cream">
                <Link to={`/tools/${t.slug}`} className="group block h-full p-7 hover:bg-cream-light">
                  <p className="label mb-3">{resourceProfile(t.path).task}</p>
                  <h2 className="text-[1.5rem] font-bold leading-[1.25] group-hover:underline group-hover:underline-offset-[0.2em]">
                    {t.title}
                  </h2>
                  <p className="mt-3 text-body">{t.description}</p>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {tools.length > 0 && filteredTools.length === 0 && <p className="ecm-library-no-results">No tools match this combination. Clear a filter or try a broader search.</p>}
      </section>
      <CTABand variant="engineer" />
    </>
  );
};

export default Tools;
